import React, { useState } from 'react';
import { Patient } from '../types/patient';
import { Brain, CheckCircle, AlertTriangle, Target, Lightbulb, Sparkles, Loader2, AlertCircle, BookOpen, X } from 'lucide-react';
import clinicalSuggestionsData from '../data/clinical_suggestions.json';

interface AISuggestionsProps {
  patient: Patient;
  compact?: boolean;
}

interface ClinicalSuggestion {
  suggestion: string;
  rationale: string;
  priority: 'high' | 'medium' | 'low';
  relevant_findings: string[];
  evidence_basis: string;
  action_items: string[];
}

interface ClinicalSuggestionsResponse {
  suggestions: ClinicalSuggestion[];
  disclaimer: string;
  generated_at: string;
  model_info: {
    model: string;
    reasoning_effort: string;
    max_tokens: number;
  };
}

type LoadingState = 'idle' | 'loading' | 'success' | 'error';

export default function AISuggestions({ patient, compact = false }: AISuggestionsProps) {
  const [loadingState, setLoadingState] = useState<LoadingState>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [clinicalData, setClinicalData] = useState<ClinicalSuggestionsResponse | null>(null);
  const [completedItems, setCompletedItems] = useState<Set<string>>(new Set());
  const [expandedSuggestions, setExpandedSuggestions] = useState<Set<number>>(new Set());
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState<number | null>(null);

  const toggleCompleted = (itemId: string) => {
    const newCompleted = new Set(completedItems);
    if (newCompleted.has(itemId)) {
      newCompleted.delete(itemId);
    } else {
      newCompleted.add(itemId);
    }
    setCompletedItems(newCompleted);
  };

  const toggleExpanded = (index: number) => {
    const newExpanded = new Set(expandedSuggestions);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedSuggestions(newExpanded);
  };

  const handleGenerateInsights = async () => {
    setLoadingState('loading');
    setErrorMessage('');

    try {
      // Check if patient has pre-loaded AI suggestions
      const preloadedSuggestions = (patient as any).aiSuggestions;

      if (preloadedSuggestions) {
        // Use pre-loaded suggestions from data_store
        await new Promise(resolve => setTimeout(resolve, 500)); // Brief loading for UX

        const data: ClinicalSuggestionsResponse = {
          suggestions: preloadedSuggestions.suggestions || [],
          disclaimer: preloadedSuggestions.disclaimer || 'AI-generated suggestions. Always verify with clinical judgment.',
          generated_at: preloadedSuggestions.generated_at || new Date().toISOString(),
          model_info: preloadedSuggestions.model_info || {
            model: 'Pre-loaded',
            reasoning_effort: 'high',
            max_tokens: 4000,
          },
        };

        setClinicalData(data);
        setLoadingState('success');
      } else {
        // Fall back to loading static clinical_suggestions.json
        await new Promise(resolve => setTimeout(resolve, 1000));
        const data: ClinicalSuggestionsResponse = clinicalSuggestionsData as ClinicalSuggestionsResponse;

        setClinicalData(data);
        setLoadingState('success');
      }
    } catch (err) {
      setLoadingState('error');
      setErrorMessage(err instanceof Error ? err.message : 'An unexpected error occurred');
      console.error('Error generating insights:', err);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-danger-600 bg-danger-100';
      case 'medium': return 'text-warning-600 bg-warning-100';
      case 'low': return 'text-medical-600 bg-medical-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'high': return <AlertTriangle className="w-5 h-5 text-red-600" />;
      case 'medium': return <Target className="w-5 h-5 text-orange-600" />;
      case 'low': return <Lightbulb className="w-5 h-5 text-blue-600" />;
      default: return <Lightbulb className="w-5 h-5 text-gray-600" />;
    }
  };

  const renderModal = () => {
    if (selectedSuggestionIndex === null || !clinicalData) return null;

    const suggestion = clinicalData.suggestions[selectedSuggestionIndex];
    const itemId = `suggestion-${selectedSuggestionIndex}`;
    const isCompleted = completedItems.has(itemId);
    const isExpanded = expandedSuggestions.has(selectedSuggestionIndex);

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={() => setSelectedSuggestionIndex(null)}>
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-start justify-between">
            <div className="flex items-center space-x-2 flex-1">
              <div className="flex-shrink-0">{getPriorityIcon(suggestion.priority)}</div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-gray-900">{suggestion.suggestion}</h3>
                <div className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(suggestion.priority)} mt-1`}>
                  {suggestion.priority} priority
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedSuggestionIndex(null)}
              className="p-1 rounded-lg hover:bg-gray-100 transition-colors flex-shrink-0 ml-2"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Rationale */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Rationale:</h4>
              <p className="text-sm text-gray-700 leading-relaxed">{suggestion.rationale}</p>
            </div>

            {/* Action Items Checklist */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Action Items Checklist:</h4>
              <ul className="space-y-2">
                {suggestion.action_items.map((action, actionIndex) => (
                  <li key={actionIndex} className="flex items-start space-x-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      id={`modal-action-${selectedSuggestionIndex}-${actionIndex}`}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-dark-500 focus:ring-brand-dark-500"
                    />
                    <label htmlFor={`modal-action-${selectedSuggestionIndex}-${actionIndex}`} className="flex-1 cursor-pointer">
                      {action}
                    </label>
                  </li>
                ))}
              </ul>
            </div>

            {/* Relevant Findings */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Relevant Findings:</h4>
              <ul className="space-y-1">
                {suggestion.relevant_findings.map((finding, findingIndex) => (
                  <li key={findingIndex} className="flex items-start space-x-2 text-sm text-gray-600">
                    <div className="w-1.5 h-1.5 bg-brand-dark-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span className="flex-1">{finding}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Evidence Basis */}
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                <BookOpen className="w-4 h-4 mr-1" />
                Evidence Basis:
              </h4>
              <p className="text-sm text-gray-600 italic">{suggestion.evidence_basis}</p>
            </div>

            {/* Mark Complete Button */}
            <div className="pt-4 border-t border-gray-200">
              <button
                onClick={() => toggleCompleted(itemId)}
                className={`w-full py-2 px-4 rounded-lg transition-colors flex items-center justify-center space-x-2 ${
                  isCompleted
                    ? 'bg-medical-100 text-medical-600 hover:bg-medical-200'
                    : 'bg-brand-dark-500 text-white hover:bg-brand-dark-600'
                }`}
              >
                <CheckCircle className="w-5 h-5" />
                <span className="font-medium">{isCompleted ? 'Marked as Complete' : 'Mark as Complete'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-1.5">
          <Brain className="w-4 h-4 text-primary-600" />
          <h2 className="text-sm font-bold text-gray-900">AI-Powered Clinical Insights</h2>
        </div>

        {loadingState === 'idle' && (
          <button
            onClick={handleGenerateInsights}
            className="flex items-center space-x-2 px-4 py-2 bg-brand-dark-500 text-white rounded-lg hover:bg-brand-dark-600 transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            <span className="text-sm font-medium">Generate AI Insights</span>
          </button>
        )}

        {loadingState === 'success' && clinicalData && (
          <button
            onClick={handleGenerateInsights}
            className="inline-flex items-center justify-center px-6 py-3 text-sm font-semibold text-primary-700 bg-primary-50 hover:bg-primary-100 rounded-lg transition-all border border-primary-200 shadow-md hover:shadow-lg"
          >
            <Sparkles className="w-5 h-5 mr-2" />
            Regenerate
          </button>
        )}
      </div>

      {loadingState === 'idle' && (
        <div className="card p-6 text-center">
          <Brain className="w-12 h-12 text-primary-300 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">Ready to Generate Clinical Insights</h3>
          <p className="text-xs text-gray-600 mb-4 max-w-md mx-auto">
            Click the button above to generate AI-powered clinical suggestions based on this patient's complete EHR data.
            This analysis uses advanced reasoning to provide evidence-based recommendations.
          </p>
          <div className="inline-flex items-center text-xs text-gray-500">
            <AlertCircle className="w-3.5 h-3.5 mr-1" />
            Powered by GPT-5 with high reasoning effort
          </div>
        </div>
      )}

      {loadingState === 'loading' && (
        <div className="card p-6 text-center">
          <Loader2 className="w-12 h-12 text-primary-600 mx-auto mb-3 animate-spin" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">Generating Clinical Insights</h3>
          <p className="text-xs text-gray-600 mb-2">
            Analyzing patient EHR data with AI...
          </p>
          <p className="text-xs text-gray-500">
            This may take 30-60 seconds
          </p>
        </div>
      )}

      {loadingState === 'error' && (
        <div className="card p-6 text-center border-2 border-red-200 bg-red-50">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h3 className="text-sm font-medium text-red-900 mb-2">Error Generating Insights</h3>
          <p className="text-xs text-red-700 mb-4">
            {errorMessage}
          </p>
          <button
            onClick={handleGenerateInsights}
            className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg transition-colors"
          >
            Try Again
          </button>
        </div>
      )}

      {loadingState === 'success' && clinicalData && (
        compact ? (
          // Compact tile view
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {clinicalData.suggestions.map((suggestion, index) => {
              const itemId = `suggestion-${index}`;
              const isCompleted = completedItems.has(itemId);

              return (
                <div
                  key={index}
                  className={`card p-3 cursor-pointer hover:shadow-md transition-shadow ${isCompleted ? 'opacity-60' : ''}`}
                  onClick={() => setSelectedSuggestionIndex(index)}
                >
                  <div className="flex items-start space-x-2 mb-2">
                    <div className="flex-shrink-0">{getPriorityIcon(suggestion.priority)}</div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm font-semibold text-gray-900 line-clamp-2">{suggestion.suggestion}</h3>
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(suggestion.priority)}`}>
                      {suggestion.priority}
                    </div>
                    <div className="text-xs text-gray-600">
                      {suggestion.action_items.length} {suggestion.action_items.length === 1 ? 'action' : 'actions'}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          // Full view mode
          <div className="space-y-2">
            {clinicalData.suggestions.map((suggestion, index) => {
              const isExpanded = expandedSuggestions.has(index);
              const itemId = `suggestion-${index}`;
              const isCompleted = completedItems.has(itemId);

              return (
                <div
                  key={index}
                  className={`card p-3 ${isCompleted ? 'opacity-60' : ''}`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2 flex-1">
                      <div className="flex-shrink-0">{getPriorityIcon(suggestion.priority)}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-semibold text-gray-900">{suggestion.suggestion}</h3>
                        <div className="flex items-center space-x-1.5 mt-0.5">
                          <div className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${getPriorityColor(suggestion.priority)}`}>
                            {suggestion.priority} priority
                          </div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => toggleCompleted(itemId)}
                      className={`p-1.5 rounded-lg transition-colors flex-shrink-0 ml-2 ${
                        isCompleted
                          ? 'bg-medical-100 text-medical-600'
                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                      }`}
                      title={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
                    >
                      <CheckCircle className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Rationale */}
                  <div className="mb-2">
                    <h4 className="text-xs font-medium text-gray-900 mb-1">Rationale:</h4>
                    <p className="text-xs text-gray-700 leading-relaxed">{suggestion.rationale}</p>
                  </div>

                  {/* Action Items Checklist */}
                  <div className="mb-2">
                    <h4 className="text-xs font-medium text-gray-900 mb-1">Action Items Checklist:</h4>
                    <ul className="space-y-1">
                      {suggestion.action_items.map((action, actionIndex) => (
                        <li key={actionIndex} className="flex items-start space-x-2 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            id={`action-${index}-${actionIndex}`}
                            className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                            onChange={(e) => {
                              // Optional: track individual action item completion
                            }}
                          />
                          <label htmlFor={`action-${index}-${actionIndex}`} className="flex-1 cursor-pointer">
                            {action}
                          </label>
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Expandable sections */}
                  <button
                    onClick={() => toggleExpanded(index)}
                    className="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center"
                  >
                    {isExpanded ? 'Hide' : 'Show'} Details
                    <svg
                      className={`w-3 h-3 ml-1 transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {isExpanded && (
                    <div className="mt-2 pt-2 border-t border-gray-200 space-y-2">
                      {/* Relevant Findings */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-900 mb-1">Relevant Findings:</h4>
                        <ul className="space-y-0.5">
                          {suggestion.relevant_findings.map((finding, findingIndex) => (
                            <li key={findingIndex} className="flex items-start space-x-1.5 text-xs text-gray-600">
                              <div className="w-1 h-1 bg-primary-400 rounded-full mt-1.5 flex-shrink-0"></div>
                              <span className="flex-1">{finding}</span>
                            </li>
                          ))}
                        </ul>
                      </div>

                      {/* Evidence Basis */}
                      <div>
                        <h4 className="text-xs font-medium text-gray-900 mb-1 flex items-center">
                          <BookOpen className="w-3 h-3 mr-1" />
                          Evidence Basis:
                        </h4>
                        <p className="text-xs text-gray-600 italic">{suggestion.evidence_basis}</p>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}

            {/* Metadata footer */}
            <div className="card p-3 bg-gray-50">
              <div className="flex items-start justify-between text-xs">
                <div>
                  <p className="text-gray-600 mb-1">
                    <strong>Generated:</strong> {new Date(clinicalData.generated_at).toLocaleString()}
                  </p>
                  <p className="text-gray-600">
                    <strong>Model:</strong> {clinicalData.model_info.model} ({clinicalData.model_info.reasoning_effort} reasoning)
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-gray-700 font-medium">
                    {completedItems.size} of {clinicalData.suggestions.length} reviewed
                  </div>
                </div>
              </div>
            </div>

            {/* Disclaimer */}
            <div className="card p-3 bg-yellow-50 border border-yellow-200">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-800">
                  {clinicalData.disclaimer}
                </p>
              </div>
            </div>
          </div>
        )
      )}

      {/* Render modal */}
      {renderModal()}
    </div>
  );
}
